import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  SimpleChanges,
  OnChanges,
  EventEmitter,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, Validators } from '@angular/forms';

type ErrorState = 'warning' | 'error';

@Component({
  selector: 'lib-phone-validator',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './phone-validator.component.html',
  styleUrls: ['./phone-validator.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PhoneValidatorComponent implements OnChanges {
  /** Inputs */
  @Input() fieldLabel = 'Phone Number';
  @Input() placeholder = 'Enter phone number';
  @Input() isSubmitted = false;
  @Input() fullWidth = true;

  /** Output */
  @Output() inputFieldData: EventEmitter<string> = new EventEmitter();

  /** Error handling */
  public errorState: ErrorState = null;
  public errorMessageText = '';

  /** FormControl with validators */
  phoneControl = new FormControl<string>('', {
    validators: [
      Validators.required,
      Validators.pattern(/^\D*(\d\D*){10,15}$/), /**  allows formatting but checks digit count */
      Validators.minLength(10), /** optional if pattern already enforces */
      Validators.maxLength(15), /** optional if pattern already enforces */
    ],
    nonNullable: true,
  });

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['isSubmitted'] &&
      changes['isSubmitted'].currentValue === true
    ) {
      this.updateErrorState();
    }
  }

  /** Trigger validation and emit value on blur */
  onBlur(): void {
    this.updateErrorState();
    const val = this.phoneControl.value;
    if (val && this.phoneControl.valid) {
      this.inputFieldData.emit(val);
    }else{
      this.inputFieldData.emit(null);
    }
  }

  /** Update error message and state */
  private updateErrorState(): void {
    const errorMessages: Record<string, string> = {
      required: 'Phone number is required.',
      pattern: 'Phone number must contain only digits.',
      minlength: 'Phone number must be at least 10 digits.',
      maxlength: 'Maximum 10 digit ia allowed for phone number.',
    };
    const errors = this.phoneControl.errors;
    if (errors) {
      const firstErrorKey = Object.keys(errors)[0];
      this.errorMessageText =
        errorMessages[firstErrorKey] || 'Invalid phone number.';
      this.errorState = this.isSubmitted ? 'error' : 'warning';
    } else {
      this.errorMessageText = '';
      this.errorState = null;
    }
  }

  /** Prevent non-numeric input */
  preventNonNumeric(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
    ];
    if (allowedKeys.includes(event.key)) return;
    if (!(event.key >= '0' && event.key <= '9')) {
      event.preventDefault();
    }
  }

  /** formating the number as per US standard */
  formatPhoneNumber(): void {
    this.errorMessageText = '';
      this.errorState = null;
    let input = this.phoneControl.value || '';
    /** Remove all non-digit characters */
    input = input.replace(/\D/g, '');
    /**Apply US format for first 10 digits */
    if (input.length <= 3) {
      input = `(${input}`;
    } else if (input.length <= 6) {
      input = `(${input.slice(0, 3)}) ${input.slice(3)}`;
    } else if (input.length <= 10) {
      input = `(${input.slice(0, 3)}) ${input.slice(3, 6)}-${input.slice(6)}`;
    } else {
      /** For more than 10 digits, keep formatting first 10 and append the rest */
      input = `(${input.slice(0, 3)}) ${input.slice(3, 6)}-${input.slice(
        6,
        10
      )} ${input.slice(10)}`;
    }
    this.phoneControl.setValue(input, { emitEvent: false });
  }
}
